<template>
    <div v-if="!featureFlags?.billing_upgrade_v2" class="upgradePlan upgradePLanCompliance">
        <template v-if="MODAL_TYPE === MODAL_TYPES.UPGRADE_PLAN_INFO">
            <change-plan-new
                ref="changePlanNew"
                :defaultPlan="plan"
                :defaultType="type"
                :hasCard="hasCard"
                :price="price"
                :resetPlan="false"
                :currentPlan="currentPlan"
                :target="target"
                :defaultPlanName="getNameDefaultPlan"
                :tablePrices="tablePrices"
                @closed="close"
                @handle-change-data="handleChangeData"
            />
        </template>

        <template v-if="MODAL_TYPE === MODAL_TYPES.UPGRADE_PLAN_BEFORE">
            <div class="info">
                <div class="infoTitle">Before You Upgrade</div>
                <form-message
                    type="is-info"
                    icon="info-circle"
                    message="To ensure uninterrupted service and avoid potential fees, please complete the required compliance steps first. "
                />
                <div class="infoConditions">
                    <div class="infoDescription">If you upgrade today you will have access to:</div>
                    <div class="infoItem">✅ Inbound & Outbound Calling</div>
                    <div class="infoItem">✅ Ringless Voicemail</div>
                    <div class="infoItem">✅ Inbound Text Messaging</div>
                    <div class="infoItem">❌ Outbound Text Messaging</div>
                </div>

                <form-checkbox v-model="terms">
                    I understand that messages
                    {{ isPFTTrialFlowToolFree ? 'from unverified Toll-Free' : 'to US-based' }}
                    numbers will be blocked by carriers until compliance is completed.
                </form-checkbox>
            </div>
        </template>

        <template v-if="MODAL_TYPE === MODAL_TYPES.UPGRADE_PLAN">
            <form-message v-if="form.errors.has('stripe')" :message="form.errors.get('stripe')" />
            <form-message
                v-else-if="form.errors.any()"
                message="Something went wrong, please try again."
            />

            <div class="upgradePlan__title">
                {{ modalTitle }}
            </div>

            <div class="upgradePlanSlider">
                <div class="upgradePlanSlider__top">
                    <div class="upgradePlanSlider__line">
                        <div
                            v-for="item in stepLine"
                            :style="{
                                width: `${100 / stepLine.length}%`,
                            }"
                        />
                    </div>
                    <Slider
                        :key="target"
                        v-model="value"
                        :format="formatChange"
                        :min="min"
                        :max="max"
                        :step="step"
                        :show-tooltip="'always'"
                        :tooltip-position="'top'"
                        :lazy="false"
                    />
                </div>
                <div class="upgradePlanSlider__bottom">
                    <span>{{ firstPlanCredits }}</span>
                    <span>{{ lastPlanCredits }} +</span>
                </div>
            </div>

            <div class="upgradePlanToggle">
                <toggle-buttons
                    :buttons="buttons"
                    :default="getNameDefaultPlan === 'annual' ? 1 : 0"
                    @toggle-buttons="toggleButtons"
                />
            </div>

            <div class="upgradePlan__info">
                <span>Get 2 months free!</span>
                <img
                    :src="`${$window.Config.cdn_url}/images/app/svg/arrow-top-ellips.svg`"
                    alt=""
                />
            </div>

            <div class="upgradePlan__content">
                <div class="upgradePlan__choose">
                    <template v-if="target === 'monthly'">
                        <upgrade-plan-item
                            v-if="!isSelfUpgrade && isLastOption"
                            :key="'custom-plan-monthly'"
                            :type="'custom-monthly'"
                            :plan="currentPlan.id"
                            :period="'monthly'"
                            :currentStep="currentStep"
                            :planList="planList"
                            :planCredit="getDefaultPlanCredit"
                            :defaultPlanName="getNameDefaultPlan"
                            :defaultTarget="defaultTarget"
                            :currentTarget="target"
                            :lastOption="true"
                            :showButton="false"
                            @handle-change-data="handleChangeData"
                            @handle-change-data-upgrade="handleChangeDataUpgrade"
                            @close="close"
                        />

                        <upgrade-plan-item
                            v-if="isSelfUpgrade || !isLastOption"
                            :key="'pro-plan-monthly'"
                            :type="'pro-plan-monthly'"
                            :plan="currentPlan.id"
                            :period="'monthly'"
                            :currentStep="currentStep"
                            :planList="planList"
                            :planCredit="getDefaultPlanCredit"
                            :defaultPlanName="getNameDefaultPlan"
                            :defaultTarget="defaultTarget"
                            :currentTarget="target"
                            :showButton="false"
                            @handle-change-data="handleChangeData"
                            @handle-change-data-upgrade="handleChangeDataUpgrade"
                            @close="close"
                        />
                        <upgrade-plan-item
                            v-if="isSelfUpgrade || !isLastOption"
                            :key="'custom-monthly'"
                            :type="'custom-monthly'"
                            :plan="'custom-monthly'"
                            :period="'monthly'"
                            :currentStep="currentStep"
                            :planList="planList"
                            :showButton="false"
                            @handle-change-data="handleChangeData"
                            @handle-change-data-upgrade="handleChangeDataUpgrade"
                            @close="close"
                        />
                    </template>
                    <template v-else>
                        <upgrade-plan-item
                            v-if="!isSelfUpgrade && isLastOption"
                            :key="'custom-plan-annual'"
                            :type="'custom-annual'"
                            :plan="currentPlan.id"
                            :period="'annual'"
                            :currentStep="currentStep"
                            :planList="planList"
                            :planCredit="getDefaultPlanCredit"
                            :defaultTarget="defaultTarget"
                            :currentTarget="target"
                            :defaultPlanName="getNameDefaultPlan"
                            :lastOption="true"
                            :showButton="false"
                            @handle-change-data="handleChangeData"
                            @handle-change-data-upgrade="handleChangeDataUpgrade"
                            @close="close"
                        />

                        <upgrade-plan-item
                            v-if="isSelfUpgrade || !isLastOption"
                            :key="'pro-plan-annual'"
                            :type="'pro-plan-annual'"
                            :plan="currentPlan.id"
                            :period="'annual'"
                            :currentStep="currentStep"
                            :planList="planList"
                            :planCredit="getDefaultPlanCredit"
                            :defaultTarget="defaultTarget"
                            :currentTarget="target"
                            :defaultPlanName="getNameDefaultPlan"
                            :showButton="false"
                            @handle-change-data="handleChangeData"
                            @handle-change-data-upgrade="handleChangeDataUpgrade"
                            @close="close"
                        />
                        <upgrade-plan-item
                            v-if="isSelfUpgrade || !isLastOption"
                            :key="'custom-annual'"
                            :type="'custom-annual'"
                            :plan="'custom-annual'"
                            :period="'annual'"
                            :currentStep="currentStep"
                            :planList="planList"
                            :showButton="false"
                            @handle-change-data="handleChangeData"
                            @handle-change-data-upgrade="handleChangeDataUpgrade"
                            @close="close"
                        />
                    </template>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import Form from 'core/Form';
import Slider from '@vueform/slider';
import EarlyUpgradeMixin from 'mixins/early-upgrade';
import OrganizationMixin from 'mixins/organization';
import UpgradePlanItem from './UpgradePlanItem.vue';
import ChangePlanNew from './ChangePlanNew/ChangePlanNew.vue';

export default {
    emits: ['handleChangeUpgradePlanTerms', 'handleChangeUpgradePlan', 'close'],
    
    components: {
        ChangePlanNew,
        UpgradePlanItem,
        Slider,
    },

    mixins: [EarlyUpgradeMixin, OrganizationMixin],

    props: {
        showPreviewPlan: { type: Boolean, default: false },
        MODAL_TYPE: { type: Number, default: 1 },
        MODAL_TYPES: { type: Object, default: () => {} },
        tablePrices: { type: Array, default: () => [] },
    },

    data() {
        return {
            form: new Form({}),
            value: 0,
            step: 1,
            min: 0,
            max: 6,
            buttons: [
                {
                    label: 'Monthly',
                    value: 'monthly',
                },
                {
                    label: 'Annually',
                    value: 'annual',
                },
            ],
            target: 'monthly',
            currentStep: 0,
            stepLine: [],
            hasCard: false,
            plan: '',
            type: '',
            price: '',
            terms: false,
        };
    },

    computed: {
        modalTitle() {
            return `How many messages will you send per ${this.target === 'monthly' ? 'month' : 'year'}?`;
        },

        isSelfUpgrade() {
            return this.$route.name === 'settings.billing.overview.self_upgrade';
        },

        firstPlanCredits() {
            if (this.isSelfUpgrade) {
                const creditsMonthly = this.proPlanMonthlyTiersForSelfUpgrade[0].credits;
                const creditsAnnual = this.proPlanAnnualTiersForSelfUpgrade[0].credits;

                const credits = this.target === 'monthly' ? creditsMonthly : creditsAnnual;

                return `${credits / 1000}k`;
            }

            const creditsMonthly = this.proPlanMonthlyTiersForUsers[0].credits;
            const creditsAnnual = this.proPlanAnnualTiersForSelfUpgrade[0].credits;
            const credits = this.target === 'monthly' ? creditsMonthly : creditsAnnual;

            if (credits < 1000) {
                return credits;
            } else {
                return `${credits / 1000}k`;
            }
        },

        lastPlanCredits() {
            if (this.isSelfUpgrade) {
                const creditsMonthly =
                    this.proPlanMonthlyTiersForSelfUpgrade[
                        this.proPlanMonthlyTiersForSelfUpgrade.length - 1
                    ].credits;
                const creditsAnnual =
                    this.proPlanAnnualTiersForSelfUpgrade[
                        this.proPlanAnnualTiersForSelfUpgrade.length - 1
                    ].credits;

                const credits = this.target === 'monthly' ? creditsMonthly : creditsAnnual;

                return `${credits / 1000}k`;
            }

            const creditsMonthly =
                this.proPlanMonthlyTiersForUsers[this.proPlanMonthlyTiersForUsers.length - 1]
                    .credits;
            const creditsAnnual =
                this.proPlanAnnualTiersForUsers[this.proPlanAnnualTiersForUsers.length - 1].credits;
            const credits = this.target === 'monthly' ? creditsMonthly : creditsAnnual;

            if (credits < 1000) {
                return credits;
            } else {
                return `${credits / 1000}k`;
            }
        },

        currentPlan() {
            return this.planList[this.currentStep];
        },

        planList() {
            if (this.target === 'monthly') {
                if (this.isSelfUpgrade) {
                    return this.proPlanMonthlyTiersForSelfUpgrade;
                }

                return this.proPlanMonthlyTiersForUsers;
            }

            if (this.isSelfUpgrade) {
                return this.proPlanAnnualTiersForSelfUpgrade;
            }

            return this.proPlanAnnualTiersForUsers;
        },

        getDefaultStep() {
            if (!this.checkOldBillingPlans) {
                return this.planList.findIndex((o) => o.id === this.getDefaultPlan.id);
            }

            return false;
        },

        defaultTarget() {
            return this.target === this.getNameDefaultPlan;
        },

        isLastOption() {
            return this.currentStep === this.max;
        },

        isPFTTrialFlowToolFree() {
            return (
                this.user.isPftTrialFlow &&
                this.isOnTrial &&
                !this.config['10dlc_banners_info']?.is_messaging_canada_only &&
                this.isTollFree
            );
        },

        isTollFree() {
            return this.number.isTollFree;
        },

        ...mapState(stores.Subscriptions, ['subscription']),
        ...mapState(stores.BillingSources, ['billingPlans']),
        ...mapState(stores.User, ['user', 'config']),
        ...mapState(stores.Number, ['number']),
    },

    watch: {
        showPreviewPlan(status) {
            this.handleChangeData(['hideFooter', !status]);
        },

        isBack(value) {
            if (value) {
                this.handleChangeData(['showPreviewPlan', false]);
            }
        },

        terms(status) {
            this.handleChangeTerms(status);
        },
    },

    methods: {
        init() {
            this.min = 0;
            this.max = this.planList.length - 1;
            this.stepLine = Array.from({ length: this.max }, () => 'line');
            this.$emit('handleChangeUpgradePlanTerms', this.terms);

            if (!this.checkOldBillingPlans) {
                this.value = this.isCanceledOrTrial ? 0 : this.getDefaultStep;
            }
        },

        reset() {
            this.$emit('handleChangeUpgradePlan', ['plan', '']);
            this.$emit('handleChangeUpgradePlan', ['type', '']);
            this.$emit('handleChangeUpgradePlan', ['price', 0]);
            this.$emit('handleChangeUpgradePlan', ['hasPlanDowngrade', false]);
            this.$emit('handleChangeUpgradePlan', ['showPreviewPlan', false]);
            this.$emit('handleChangeUpgradePlan', ['hasCard', false]);
        },

        close() {
            this.handleChangeData(['showPreviewPlan', false]);
            this.$emit('close');
        },

        formatChange(value) {
            this.reset();

            this.currentStep = Math.round(value);

            const credits = this.currentPlan.credits;

            if (credits < 1000) {
                return credits;
            } else {
                if (this.isLastOption) return `${credits / 1000}k+`;

                return `${credits / 1000}k`;
            }
        },

        toggleButtons({ value }) {
            this.target = value;
        },

        handleChangeData([key, value]) {
            if (key === 'hasCard') {
                this[key] = value;
            } else {
                this.$emit('handleChangeUpgradePlan', [key, value]);
            }
        },

        handleChangeDataUpgrade([key, value]) {
            this[key] = value;
            this.$emit('handleChangeUpgradePlan', [key, value]);
        },

        handleChangeTerms(status) {
            this.$emit('handleChangeUpgradePlanTerms', status);
        },
    },

    mounted() {
        if (!this.featureFlags?.billing_upgrade_v2) {
            this.init();
        }
    },
};
</script>

<style lang="scss">
@import './styles.module.scss';
</style>
